import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import PageHeader from '../innovher/PageHeader';
import { EditorialIntro } from '../innovher/SplitSection';
import ApplyCta from '../innovher/ApplyCta';
import SEO from '../SEO';
import { STOCK } from '../../lib/images';

const JOURNEY = [
  'Strategic guidance',
  'Founder capability',
  'Market access',
  'Mentorship',
  'Business networks',
  'Investment readiness',
  'Capital & collaboration',
];

const IMPACT_STATS = [
  { value: '81cr.', label: "InnovHer's Portfolio valuation" },
  { value: '80cr.+', label: 'Revenue generated' },
  { value: '08', label: 'Studio ventures' },
  { value: '64', label: 'National mentors' },
];

/** InnovACE portfolio, logos when available in /public/logo */
const PORTFOLIO = [
  { name: 'Reroute', src: '/logo/reroute.webp' },
  { name: 'Sanchiconnect', src: '/logo/sanchi.webp' },
  { name: 'FixPapa', src: '/logo/fix_papa.jpg' },
  { name: 'Saaskart', src: '/logo/saaskart.jpg' },
  { name: 'Edulyst' },
  { name: 'Edinbox', src: '/logo/edinbox.jpg' },
  { name: 'Kavadea', src: '/logo/kava.jpeg' },
  { name: 'Learn & build', src: '/logo/kearn&build.jpg' },
  { name: 'Aaklan', src: '/logo/aaklan.jpg' },
  { name: 'Rahavaas Dekho', src: '/logo/rahavaas.jpg' },
  { name: 'The Project Cafe', src: '/logo/tpc.jpg' },
  { name: 'Allure', src: '/logo/allure.jpg' },
  { name: 'Hobnob', src: '/logo/hobnob.jpg' },
  { name: "Aroma's Crest" },
  { name: 'Anyme' },
  { name: 'Turtle Tales', src: '/logo/talli_tales.jpg' },
];

function SectionEyebrow({ children }) {
  return (
    <div className="text-[#ff8ec5] text-xs tracking-[0.3em] uppercase mb-4">{children}</div>
  );
}

export default function InnovAcePage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const els = document.querySelectorAll('.reveal-up');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-revealed');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <SEO
        title="InnovACE | Accelerating Ambition into Scale"
        description="InnovACE is InnovHer's accelerator for founders ready to move from potential to measurable growth, an intensive journey of validation, mentorship and commercial readiness."
        keywords="InnovACE, InnovHer accelerator, startup accelerator India, portfolio acceleration, venture growth"
      />

      {/* Header / Purpose Section */}
      <section className="relative bg-[#08080a] pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-20 lg:pb-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <EditorialIntro
            className="reveal-up"
            eyebrow="InnovACE"
            logo="/new_images/logos/innovace.webp"
            title={
              <>
                Accelerating ambition into <span className="text-[#d42b92]">scale</span>.
              </>
            }
            lead="InnovACE is an accelerator for founders and ventures ready to move from potential to measurable growth."
          >
            <p>
              Built around an intensive 18-month journey, InnovACE combines strategic guidance,
              founder capability, market access, mentorship, business networks, investment readiness
              and opportunities for capital and collaboration.
            </p>
          </EditorialIntro>
        </div>
      </section>

      <section className="relative bg-[#08080a] py-16 lg:py-24 border-t border-white/5 overflow-hidden">
        <div className="absolute top-1/2 right-0 w-[420px] h-[420px] rounded-full bg-[#d42b92]/10 blur-[140px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left: Text content */}
            <div className="reveal-up">
              <SectionEyebrow>The journey</SectionEyebrow>
              <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
                An intensive{' '}
                <span className="text-[#d42b92]">18-month</span> path
              </h2>
              <p className="mt-6 text-white/60 text-base sm:text-lg leading-relaxed">
                Rather than following a one-size-fits-all accelerator model, each venture progresses
                against its own priorities, milestones and growth trajectory, supported by the wider
                InnovHer ecosystem.
              </p>
              <p className="mt-4 text-white font-semibold text-lg">
                With a growing portfolio of ventures and alumni, InnovACE is designed around one
                central objective -{' '}
                <span className="text-[#ff8ec5] italic">
                  help promising businesses become stronger, more resilient and ready for their next
                  stage.
                </span>
              </p>

              <div className="mt-8 flex flex-wrap gap-2.5">
                {JOURNEY.map((label) => (
                  <span
                    key={label}
                    className="px-4 py-2.5 rounded-full text-sm border border-white/10 bg-white/[0.03] text-white/75"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>

            {/* Right: Image */}
            <div className="reveal-up relative">
              <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/60 group">
                <img
                  src="/new_images/innovace-mentorship.webp"
                  alt="InnovACE 18-month accelerator journey"
                  className="w-full h-full object-cover aspect-[4/3] group-hover:scale-105 transition-transform duration-700 ease-out"
                  loading="lazy"
                />
                {/* Subtle pink tint overlay on bottom */}
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#d42b92]/20 to-transparent pointer-events-none" />
              </div>
              {/* Decorative glow behind image */}
              <div className="absolute -inset-4 -z-10 rounded-3xl bg-[#d42b92]/8 blur-2xl pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      <section className="relative bg-[#08080a] py-16 lg:py-24 border-t border-white/5 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[640px] h-[280px] rounded-full bg-[#d42b92]/12 blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="reveal-up max-w-3xl mb-10">
            <SectionEyebrow>Portfolio</SectionEyebrow>
            <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
              Ventures and alumni across the{' '}
              <span className="text-[#d42b92]">ecosystem</span>
            </h2>
            <p className="mt-5 text-white/55 text-base sm:text-lg leading-relaxed">
              A growing portfolio of ventures supported through InnovACE, each on its own path to
              the next stage.
            </p>
          </div>

          <div className="reveal-up grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
            {PORTFOLIO.map((co) => (
              <div
                key={co.name}
                className="group aspect-[5/3] rounded-2xl border border-white/10 bg-white/[0.97] px-4 py-3 flex flex-col items-center justify-center gap-2 hover:border-[#d42b92]/50 transition-colors"
                title={co.name}
              >
                {co.src ? (
                  <img
                    src={co.src}
                    alt={co.name}
                    className="max-h-24 w-auto max-w-full object-contain opacity-90 group-hover:opacity-100 transition-opacity"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : null}
                <span className="text-[11px] sm:text-xs font-medium text-black/70 text-center leading-snug">
                  {co.name}
                </span>

              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-[#08080a] pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="reveal-up rounded-[2rem] border border-[#d42b92]/30 bg-gradient-to-br from-[#d42b92]/20 via-black to-black p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/3 w-[420px] h-[420px] bg-[#d42b92]/20 rounded-full blur-[120px] pointer-events-none" />
            <div className="relative z-10 max-w-2xl">
              <SectionEyebrow>Apply to InnovACE</SectionEyebrow>
              <h2 className="text-3xl md:text-4xl font-semibold text-white leading-tight">
                Ready for your next stage?
              </h2>
              <p className="mt-4 text-white/65 text-lg leading-relaxed">
                Join the InnovACE cohort, the application form is managed from the admin
                dashboard. Acceptance moves founder and startup into onboarding.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <ApplyCta to="/apply" label="Apply to InnovAce" size="lg" />
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 text-[#ff8ec5] font-medium hover:text-white transition-colors"
                >
                  Contact Us <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
